import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Box, Slider, Typography } from '@mui/material';
import {
  addDays,
  differenceInCalendarDays,
  format,
  startOfDay,
  subYears,
} from 'date-fns';

const RANGE_YEARS = 3;

/** Parse API date-only strings as local calendar dates (avoid parseISO UTC shift). */
const parseDateOnly = (dateStr) => {
  if (!dateStr) return null;
  const part = String(dateStr).split('T')[0];
  const [year, month, day] = part.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = startOfDay(new Date(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateOnlyString = (date) => format(date, 'yyyy-MM-dd');

const formatSliderLabel = (rangeStart, dayOffset) =>
  format(addDays(rangeStart, dayOffset), 'MMM d, yyyy');

const offsetsFromSubmitted = (submittedFrom, submittedTo, rangeStart, maxOffset) => {
  const fromDate = parseDateOnly(submittedFrom);
  const toDate = parseDateOnly(submittedTo);

  const lo = fromDate
    ? differenceInCalendarDays(fromDate, rangeStart)
    : 0;
  const hi = toDate
    ? differenceInCalendarDays(toDate, rangeStart)
    : maxOffset;

  const clampedLo = Math.max(0, Math.min(lo, maxOffset));
  const clampedHi = Math.max(0, Math.min(hi, maxOffset));
  return [Math.min(clampedLo, clampedHi), Math.max(clampedLo, clampedHi)];
};

const NpsSubmittedDateRangeSlider = ({ submittedFrom, submittedTo, onChange }) => {
  const rangeEnd = useMemo(() => startOfDay(new Date()), []);
  const rangeStart = useMemo(() => startOfDay(subYears(rangeEnd, RANGE_YEARS)), [rangeEnd]);
  const maxOffset = useMemo(
    () => Math.max(0, differenceInCalendarDays(rangeEnd, rangeStart)),
    [rangeEnd, rangeStart]
  );

  const committedOffsets = useMemo(
    () => offsetsFromSubmitted(submittedFrom, submittedTo, rangeStart, maxOffset),
    [submittedFrom, submittedTo, rangeStart, maxOffset]
  );

  const [sliderValue, setSliderValue] = useState(committedOffsets);

  useEffect(() => {
    setSliderValue(committedOffsets);
  }, [committedOffsets]);

  const isFullRange = !submittedFrom && !submittedTo;

  const commitOffsets = useCallback(
    (lo, hi) => {
      if (lo <= 0 && hi >= maxOffset) {
        onChange('', '');
        return;
      }
      onChange(
        toDateOnlyString(addDays(rangeStart, lo)),
        toDateOnlyString(addDays(rangeStart, hi))
      );
    },
    [maxOffset, onChange, rangeStart]
  );

  const handleChange = useCallback((_event, newValue) => {
    const [lo, hi] = newValue;
    setSliderValue([lo, hi]);
  }, []);

  const handleChangeCommitted = useCallback(
    (_event, newValue) => {
      const [lo, hi] = newValue;
      setSliderValue([lo, hi]);
      commitOffsets(lo, hi);
    },
    [commitOffsets]
  );

  const valueLabelFormat = useCallback(
    (dayOffset) => formatSliderLabel(rangeStart, dayOffset),
    [rangeStart]
  );

  const [lo, hi] = sliderValue;

  return (
    <Box sx={{ width: '100%', px: 1, pt: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {lo <= 0 && hi >= maxOffset && isFullRange
          ? 'Drag either end of the slider to limit submission dates'
          : `${formatSliderLabel(rangeStart, lo)} – ${formatSliderLabel(rangeStart, hi)}`}
      </Typography>
      <Slider
        value={sliderValue}
        onChange={handleChange}
        onChangeCommitted={handleChangeCommitted}
        min={0}
        max={maxOffset}
        disableSwap
        valueLabelDisplay="auto"
        valueLabelFormat={valueLabelFormat}
        size="small"
        sx={{ mt: 0.5, mb: 0.25 }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.5 }}>
        <Typography variant="caption" color="text.disabled">
          {format(rangeStart, 'MMM yyyy')}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {format(rangeEnd, 'MMM yyyy')}
        </Typography>
      </Box>
    </Box>
  );
};

export default NpsSubmittedDateRangeSlider;
